/* ═══════════════════════════════════════════════════════════════════
   NO HANDOUTS — homepage entrance animation
   A faithful port of the approved preview.

   Layout is CSS. The JS only does four things: measure the sticky
   header, size the WebGL canvas, pin the photograph to the wordmark,
   and run the reveal timeline. Nothing here computes layout positions —
   an earlier version did, and it failed in ways that were hard to see.

   Two independent layers:
     1. DOM timeline (type, rule, tagline, chrome). Runs unconditionally.
     2. WebGL mark. Runs only if THREE loaded and the GPU cooperates.
   Layer 1 never depends on layer 2, so the page is never blank.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* the mark, in its real proportions — trimmed master 936 x 408 */
  var S = 1 / 408;
  var BARS = [
    { w: 936 * S, h: 50 * S, y: (204 -  25.0) * S },
    { w: 602 * S, h: 35 * S, y: (204 - 232.0) * S },
    { w: 362 * S, h: 20 * S, y: (204 - 397.5) * S }
  ];
  var DEPTH = 0.085;
  /* how high the mark floats. 0.66 sat it on the collar; 0.92 seats it
     inside the hood opening. World units — scales with the viewport, so the
     mark and the hood keep the same relationship at any size.
     A fixed world value only holds the mark still against the *viewport*,
     not against the garment. The photo stops growing at its 1420px cap while
     the mark keeps scaling with the hero's height, so the two drift: measured
     across four window sizes, the top bar landed anywhere from 0.10 to 0.27
     of the way down the photo — inside the hood on a tall window, below the
     collar on a short one. Every scheme now solves for the hood each layout;
     GROUP_Y is only the fallback for when the photograph is switched off and
     there is no garment to seat against. */
  var GROUP_Y = 0.92;

  /* where the top bar's upper edge should sit, as a fraction of the photo's
     height. Both crops frame the garment identically — top of the hood at
     0.118 in each — so one value seats the mark the same way on either, and
     the two schemes read as the same lockup. */
  var MARK_SEAT = 0.140;

  /* geometry of the fixed camera: fov 30° at z 6.4 shows this many world
     units of height, and the group is scaled to 0.34 */
  var VIEW_H = 2 * 6.4 * Math.tan(15 * Math.PI / 180);
  var GROUP_SCALE = 0.34;
  var RATIO = 0.643;              /* the mark's own width ratio — drives the timing */
  var BAR_DUR   = [980, 980 * RATIO, 980 * RATIO * RATIO];
  var BAR_DELAY = [220, 470, 690];

  function NhIntro(root) {
    var self     = this;
    this.root    = root;
    this.canvas  = root.querySelector('[data-nh-scene]');
    this.photo   = root.querySelector('[data-nh-photo]');
    this.wordEl  = root.querySelector('[data-nh-word]');
    this.ruleEl  = root.querySelector('[data-nh-rule]');
    this.tagEl   = root.querySelector('[data-nh-tag]');
    this.sigEl   = root.querySelector('[data-nh-signal]');
    this.cueEl   = root.querySelector('[data-nh-cue]');
    this.ctaEl   = root.querySelector('[data-nh-cta]');
    this.chrome  = Array.prototype.slice.call(root.querySelectorAll('[data-nh-chrome]'));
    this.timers  = [];
    this.raf     = null;

    this.buildLetters();
    this.buildFallback();
    this.measureHeader();
    this.bind();

    this.initGL();
    this.matchPhotoToPalette();
    this.sizePhoto();
    this.placePhoto();
    this.debug();

    /* the webfont changes the wordmark's box — re-pin once it lands */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.onResize(); });
    }

    if (this.shouldSkip()) { this.finish(); return; }
    this.reset();
  }

  /* ── once-per-session gate ─────────────────────────────────────── */
  NhIntro.prototype.shouldSkip = function () {
    if (this.root.dataset.once !== 'true') return false;
    try {
      if (sessionStorage.getItem('nh-intro-played') === '1') return true;
      sessionStorage.setItem('nh-intro-played', '1');
    } catch (e) { /* private mode — just play it */ }
    return false;
  };

  /* ── split the wordmark into animatable letters ────────────────── */
  NhIntro.prototype.buildLetters = function () {
    var text = (this.wordEl.getAttribute('data-text') || '').trim();
    this.wordEl.textContent = '';

    /* one readable copy for assistive tech — the split letters would
       otherwise be announced one character at a time */
    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = text;
    this.wordEl.appendChild(sr);

    for (var i = 0; i < text.length; i++) {
      var s = document.createElement('span');
      s.setAttribute('aria-hidden', 'true');
      if (text[i] === ' ') { s.className = 'nh-intro__sp'; }
      else { s.className = 'nh-intro__ltr'; s.textContent = text[i]; }
      this.wordEl.appendChild(s);
    }
    this.letters = Array.prototype.slice.call(this.wordEl.querySelectorAll('.nh-intro__ltr'));
  };

  /* ── flat CSS mark, shown only when WebGL is unavailable ───────── */
  NhIntro.prototype.buildFallback = function () {
    var box = this.root.querySelector('[data-nh-fallback]');
    if (!box) return;
    var unit = Math.min(340, window.innerWidth * 0.4);
    box.innerHTML = '';
    BARS.forEach(function (b, i) {
      var el = document.createElement('b');
      el.style.width  = (b.w / BARS[0].w * unit) + 'px';
      el.style.height = (b.h / BARS[0].w * unit) + 'px';
      if (i === BARS.length - 1) el.style.marginBottom = '0';
      box.appendChild(el);
    });
  };

  /* ── the sticky header eats vertical space; measure it ─────────── */
  NhIntro.prototype.measureHeader = function () {
    var h = document.getElementById('shopify-section-header');
    var px = h ? Math.round(h.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--nh-header-h', px + 'px');
  };

  /* has the section actually been laid out yet? In the theme editor it
     gets inserted and measured before the iframe resolves its height. */
  NhIntro.prototype.laidOut = function () {
    return this.root.clientHeight > 200 && this.root.clientWidth > 100;
  };

  /* ── garment width ────────────────────────────────────────────────
     Was a pure-CSS min(1420px, 116vw, calc(... 100svh ...)). A browser
     without svh treats the whole min() as invalid, which takes the width
     with it and collapses the photo to nothing. Same maths, done here. */
  NhIntro.prototype.sizePhoto = function () {
    var H = this.root.clientHeight;
    var W = this.root.clientWidth;
    if (!W) return;
    /* the preview capped on width alone; the height term only bites on
       an unusually short window, where the hood would be cropped off */
    /* One rule for every screen. Aim for the garment to fill ~80% of the
       hero height (which is what desktop had), never wider than the source
       art, never so tall that the hood is cropped away on a short window. */
    var px = Math.min(1420, 1.9 * H, Math.max(1.16 * W, 1.2 * H));
    if (isFinite(px) && px > 0) {
      this.root.style.setProperty('--nh-photo-w', Math.round(px) + 'px');
    }
  };

  /* ── site-wide light palette: swap to the white hoodie crop ─────
     The section renders its src server-side, so a client-side palette
     switch has to repoint it here. */
  NhIntro.prototype.matchPhotoToPalette = function () {
    if (!this.photo) return;
    var light = this.root.classList.contains('nh-intro--light') ||
                document.documentElement.classList.contains('nh-light');
    var want = this.photo.getAttribute(light ? 'data-src-light' : 'data-src-dark');
    if (!want) return;                            /* a custom photo, or old markup */
    var src = this.photo.getAttribute('src') || '';
    /* compare without the ?v= so an unchanged asset is never re-fetched */
    if (src.split('?')[0] === want.split('?')[0]) return;
    this.photo.setAttribute('src', want);
  };

  /* ── pin the crop so the type lands where the print was ─────────── */
  NhIntro.prototype.placePhoto = function () {
    if (!this.photo || !this.wordEl || !this.laidOut()) return;
    var r = this.wordEl.getBoundingClientRect();
    var c = this.root.getBoundingClientRect();
    this.photo.style.top = ((r.top - c.top) + r.height / 2) + 'px';
    this.seatMark();
  };

  /* ── keep the mark in the hood at every window size ───────────────
     Solves for the group's world y so the top bar's upper edge lands on
     MARK_SEAT of the photo. The projection is linear in world y for a
     camera at a fixed z, so this is exact rather than iterative.        */
  NhIntro.prototype.seatMark = function () {
    if (!this.markSeat || !this.group || !this.photo || !this.laidOut()) return;
    var H = this.root.clientHeight;
    var pr = this.photo.getBoundingClientRect();
    var cr = this.root.getBoundingClientRect();
    if (!pr.height) return;                       /* photo not decoded yet */

    var pxPerUnit = H / VIEW_H;
    /* top bar's upper edge, in world units above the group's origin */
    var barTop = GROUP_SCALE * (BARS[0].y + BARS[0].h / 2);
    var wantPx = (pr.top - cr.top) + this.markSeat * pr.height;

    var y = (H / 2 - wantPx) / pxPerUnit - barTop;
    /* a guard, not a design choice: a mid-layout measurement can hand us
       something absurd, and a mark parked off-screen is worse than a
       slightly mis-seated one */
    this.groupY = Math.min(Math.max(y, 0.2), 1.7);

    if (this.bars) {
      this.group.position.y = this.groupY;
      this.renderOnce();
    }
  };

  NhIntro.prototype.bind = function () {
    var self = this;
    this.onResize = function () {
      self.measureHeader();
      self.buildFallback();
      self.resizeGL();
      self.sizePhoto();
      self.placePhoto();
      self.debug();
    };
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', function () { self.onResize(); });

    /* The window never resizes inside the theme editor, but the section's
       own box does — when the iframe settles, when the panel opens. */
    if (window.ResizeObserver) {
      var pending = false;
      this.ro = new ResizeObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; self.onResize(); });
      });
      this.ro.observe(this.root);
    }

    if (this.cueEl) {
      this.cueEl.addEventListener('click', function () {
        var next = self.root.closest('.shopify-section');
        next = next && next.nextElementSibling;
        var y = next
          ? next.getBoundingClientRect().top + window.pageYOffset
          : self.root.getBoundingClientRect().bottom + window.pageYOffset;
        window.scrollTo({ top: y, behavior: REDUCE ? 'auto' : 'smooth' });
      });
    }
  };

  /* ── DOM timeline ──────────────────────────────────────────────── */
  NhIntro.prototype.clearTimers = function () {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  };
  NhIntro.prototype.at = function (ms, fn) {
    this.timers.push(setTimeout(fn, ms));
  };

  NhIntro.prototype.domReset = function () {
    var self = this;
    this.clearTimers();
    this.wordEl.style.opacity = 0;
    this.letters.forEach(function (l) {
      l.style.transition = 'none';
      l.style.opacity = 0;
      l.style.transform = 'translateY(14px)';
      l.style.filter = 'blur(6px)';
    });
    [this.tagEl, this.ruleEl].forEach(function (e) {
      if (!e) return;
      e.style.transition = 'none';
      e.style.opacity = 0;
    });
    if (this.ruleEl) this.ruleEl.style.width = '0px';
    this.chrome.forEach(function (e) { e.style.transition = 'none'; e.style.opacity = 0; });
    if (this.sigEl) { this.sigEl.style.transition = 'none'; this.sigEl.style.width = '0'; }
    if (this.photo) this.photo.classList.remove('is-on');
    if (this.cueEl) { this.cueEl.style.transition = 'none'; this.cueEl.style.opacity = 0; }
    if (this.ctaEl) { this.ctaEl.style.transition = 'none'; this.ctaEl.style.opacity = 0; }
    this.placePhoto();

    requestAnimationFrame(function () {
      if (REDUCE) self.finish(); else self.domSchedule();
    });
  };

  NhIntro.prototype.domSchedule = function () {
    var self = this;

    this.at(260, function () {
      if (!self.sigEl) return;
      self.sigEl.style.transition = 'width 2.1s cubic-bezier(.22,1,.36,1)';
      self.sigEl.style.width = '100%';
    });

    this.at(1250, function () {
      self.sizePhoto();
      self.placePhoto();
      if (self.photo) self.photo.classList.add('is-on');
    });

    this.at(1450, function () {
      self.wordEl.style.opacity = 1;
      self.letters.forEach(function (l, i) {
        var d = i * 0.035;
        l.style.transition =
          'opacity .62s ease ' + d + 's, ' +
          'transform .78s cubic-bezier(.16,1,.3,1) ' + d + 's, ' +
          'filter .62s ease ' + d + 's';
        l.style.opacity = 1;
        l.style.transform = 'translateY(0)';
        l.style.filter = 'blur(0)';
      });
    });

    this.at(2250, function () {
      if (self.ruleEl) {
        self.ruleEl.style.transition = 'opacity .8s ease, width 1.1s cubic-bezier(.16,1,.3,1)';
        self.ruleEl.style.opacity = 1;
        self.ruleEl.style.width = 'min(190px, 26vw)';
      }
      if (self.tagEl) {
        self.tagEl.style.transition = 'opacity 1s ease .25s';
        self.tagEl.style.opacity = 1;
      }
    });

    this.at(2850, function () {
      self.chrome.forEach(function (e, i) {
        e.style.transition = 'opacity 1.1s ease ' + (i * 0.09) + 's';
        e.style.opacity = 0.75;
      });
    });

    /* the CTA arrives just after the tagline settles, before the cue */
    this.at(2950, function () {
      if (!self.ctaEl) return;
      self.ctaEl.style.transition = 'opacity 1s ease';
      self.ctaEl.style.opacity = 1;
    });

    this.at(3300, function () {
      if (!self.cueEl) return;
      self.cueEl.style.transition = 'opacity 1.2s ease';
      self.cueEl.style.opacity = 1;
    });
  };

  /* resting state — reduced motion, or the once-per-session skip */
  NhIntro.prototype.finish = function () {
    this.clearTimers();
    this.wordEl.style.opacity = 1;
    this.letters.forEach(function (l) {
      l.style.transition = 'none';
      l.style.opacity = 1;
      l.style.transform = 'none';
      l.style.filter = 'none';
    });
    if (this.ruleEl) { this.ruleEl.style.opacity = 1; this.ruleEl.style.width = 'min(190px, 26vw)'; }
    if (this.tagEl)  this.tagEl.style.opacity = 1;
    this.chrome.forEach(function (e) { e.style.opacity = 0.75; });
    if (this.sigEl)  this.sigEl.style.width = '100%';
    if (this.photo)  this.photo.classList.add('is-on');
    if (this.cueEl)  this.cueEl.style.opacity = 1;
    if (this.ctaEl)  this.ctaEl.style.opacity = 1;
    this.placePhoto();
    if (this.bars) {
      this.bars.forEach(function (b) { b.scale.set(1, 1, 1); b.rotation.x = 0; });
      this.group.rotation.set(0, 0, 0);
      this.group.position.set(0, this.groupY, 0);
      this.camera.position.z = 6.4;
      this.renderOnce();
    }
  };

  /* ── WebGL mark ────────────────────────────────────────────────── */
  NhIntro.prototype.initGL = function () {
    if (typeof window.THREE === 'undefined' || !this.canvas) {
      this.root.classList.add('nh-intro--nogl');
      return;
    }
    var THREE = window.THREE;
    try {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    } catch (e) {
      this.root.classList.add('nh-intro--nogl');
      return;
    }
    var warm  = this.root.classList.contains('nh-intro--warm');
    /* the section can be set to light on its own, or the whole site can be */
    var light = this.root.classList.contains('nh-intro--light') ||
                document.documentElement.classList.contains('nh-light');
    this.groupY   = GROUP_Y;
    this.markSeat = MARK_SEAT;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.18;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    this.camera.position.set(0, 0, 6.4);

    this.group = new THREE.Group();
    this.group.scale.setScalar(0.34);
    this.scene.add(this.group);

    /* On an off-white page the mark inverts: a dark body needs a tighter,
       glossier surface or it reads as a flat silhouette. */
    var mat = new THREE.MeshStandardMaterial({
      color:     light ? 0x0f0d0c : (warm ? 0xffedd7 : 0xdcd6cb),
      metalness: light ? 0.16 : 0.48,
      roughness: light ? 0.34 : 0.44
    });

    function beveledBar(w, h, d) {
      /* chamfer, never more than 17% of bar height — softens the edge
         without reading as a rounded rectangle */
      var bev = Math.min(h * 0.17, d * 0.28, 0.007);
      var sw = w - 2 * bev, sh = h - 2 * bev;
      var shape = new THREE.Shape();
      shape.moveTo(-sw / 2, -sh / 2);
      shape.lineTo( sw / 2, -sh / 2);
      shape.lineTo( sw / 2,  sh / 2);
      shape.lineTo(-sw / 2,  sh / 2);
      shape.closePath();
      var g = new THREE.ExtrudeGeometry(shape, {
        depth: Math.max(0.001, d - 2 * bev),
        bevelEnabled: true,
        bevelThickness: bev,
        bevelSize: bev,
        bevelSegments: 3,
        curveSegments: 1
      });
      g.center();
      return g;
    }

    var group = this.group;
    this.bars = BARS.map(function (b) {
      var m = new THREE.Mesh(beveledBar(b.w, b.h, DEPTH), mat);
      m.position.set(0, b.y, 0);
      m.castShadow = true;
      m.receiveShadow = true;
      group.add(m);
      return m;
    });

    this.scene.add(new THREE.AmbientLight(0xffffff, light ? 0.42 : 0.18));

    var key = new THREE.DirectionalLight(
      light ? 0xffffff : (warm ? 0xffd9a8 : 0xfff2e0), light ? 1.5 : 2.05);
    key.position.set(1.5, 2.6, 4.6);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -3;  key.shadow.camera.right = 3;
    key.shadow.camera.top  =  3;  key.shadow.camera.bottom = -3;
    key.shadow.camera.near = 0.5; key.shadow.camera.far = 14;
    key.shadow.radius = 8;
    key.shadow.bias = -0.0012;
    this.scene.add(key);

    /* invisible plane that renders only the shadow the bars cast */
    var catcher = new THREE.Mesh(
      new THREE.PlaneGeometry(14, 14),
      new THREE.ShadowMaterial({ opacity: light ? 0.0 : 0.5 })
    );
    catcher.position.z = -0.26;
    catcher.receiveShadow = true;
    this.group.add(catcher);

    var rim = new THREE.DirectionalLight(
      light ? 0xffffff : (warm ? 0xdc5000 : 0x8fa6c8), light ? 1.1 : (warm ? 0.9 : 1.5));
    rim.position.set(-3.4, -1.4, 1.6);
    this.scene.add(rim);

    var fill = new THREE.DirectionalLight(0xffffff, 0.38);
    fill.position.set(0, -2.6, 2.2);
    this.scene.add(fill);

    this.resizeGL();
  };

  NhIntro.prototype.resizeGL = function () {
    if (!this.renderer) return;
    var w = this.root.clientWidth;
    var h = this.root.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.fov = 30;          /* same on every screen: GROUP_Y and the
                                     hood alignment both depend on it */
    this.camera.updateProjectionMatrix();
    this.renderOnce();
  };

  NhIntro.prototype.renderOnce = function () {
    if (this.renderer) this.renderer.render(this.scene, this.camera);
  };

  /* ── run ───────────────────────────────────────────────────────── */
  NhIntro.prototype.reset = function () {
    var self = this;
    this.t0 = (window.performance || Date).now();
    if (this.bars) {
      this.bars.forEach(function (b) { b.scale.set(0.001, 1, 1); b.rotation.x = -Math.PI / 2; });
      this.group.rotation.set(0, 0, 0);
    }
    this.domReset();
    if (REDUCE) return;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(function (n) { self.frame(n); });
  };

  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }
  function easeIO(t)  { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  NhIntro.prototype.frame = function (now) {
    var self = this;
    if (!this.renderer) return;
    var t = now - this.t0;

    this.bars.forEach(function (b, i) {
      var local = Math.min(Math.max((t - BAR_DELAY[i]) / BAR_DUR[i], 0), 1);
      var e = easeOut(local);
      b.scale.x = Math.max(0.001, e);                    /* opens from the centre out */
      b.rotation.x = -Math.PI / 2 * (1 - easeIO(local)); /* edge-on, rotating flat */
    });

    /* the whole lockup settles with a slow drift */
    var settle = Math.min(Math.max((t - 700) / 2600, 0), 1);
    this.group.rotation.y = (1 - easeOut(settle)) * -0.42 + Math.sin(now / 5200) * 0.035;
    this.group.rotation.x = (1 - easeOut(settle)) *  0.24 + Math.sin(now / 6900) * 0.018;
    this.group.position.set(0, this.groupY + Math.sin(now / 6100) * 0.006, 0);
    this.camera.position.z = 6.4 + (1 - easeOut(settle)) * 0.8;

    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(function (n) { self.frame(n); });
  };

  /* ── ?nhdebug=1 — on-page readout, off for every normal visitor ── */
  NhIntro.prototype.debug = function () {
    if (location.search.indexOf('nhdebug') === -1) return;
    var box = this.dbg;
    if (!box) {
      box = this.dbg = document.createElement('pre');
      box.style.cssText = 'position:fixed;top:8px;left:8px;z-index:99999;margin:0;' +
        'padding:8px 10px;background:rgba(0,0,0,.85);color:#0f0;font:11px/1.45 monospace;' +
        'white-space:pre;pointer-events:none;border:1px solid #0f0;max-width:46vw';
      document.body.appendChild(box);
    }
    var r = this.root.getBoundingClientRect();
    var c = this.canvas;
    var rel = function (el) {
      if (!el) return 'none';
      var b = el.getBoundingClientRect();
      return Math.round(b.top - r.top) + ' h' + Math.round(b.height);
    };
    var sp = this.root.querySelector('.nh-intro__spacer');
    box.textContent = [
      'vp        ' + innerWidth + ' x ' + innerHeight + '  dpr ' + devicePixelRatio,
      'hero      ' + Math.round(r.width) + ' x ' + Math.round(r.height),
      'header    ' + getComputedStyle(document.documentElement).getPropertyValue('--nh-header-h'),
      'photo-w   ' + getComputedStyle(this.root).getPropertyValue('--nh-photo-w'),
      'spacerH   ' + (sp ? getComputedStyle(sp).height : '-'),
      'canvasbuf ' + (c ? c.width + ' x ' + c.height : '-'),
      'canvasbox ' + (c ? Math.round(c.getBoundingClientRect().width) + ' x ' +
                          Math.round(c.getBoundingClientRect().height) : '-'),
      'word      ' + rel(this.wordEl) + '  fs ' + getComputedStyle(this.wordEl).fontSize,
      'photo     ' + rel(this.photo),
      'cta       ' + rel(this.ctaEl),
      'cue       ' + rel(this.cueEl),
      'laidOut   ' + this.laidOut() + '   nogl ' + this.root.classList.contains('nh-intro--nogl'),
      'light     ' + (this.markSeat ? 'yes' : 'no') + '   seat ' + this.markSeat +
                     '   groupY ' + (this.groupY || 0).toFixed(3),
      'src       ' + ((this.photo && this.photo.currentSrc) || '-').split('/').pop(),
      'natural   ' + (this.photo ? this.photo.naturalWidth + ' x ' + this.photo.naturalHeight : '-')
    ].join('\n');
  };

  NhIntro.prototype.destroy = function () {
    this.clearTimers();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.ro) this.ro.disconnect();
    window.removeEventListener('resize', this.onResize);
    if (this.renderer) this.renderer.dispose();
  };

  /* ── boot ──────────────────────────────────────────────────────── */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-nh-intro]').forEach(function (el) {
      if (el.dataset.nhReady === '1') return;
      el.dataset.nhReady = '1';
      el.__nh = new NhIntro(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* theme editor: tear down and re-run when the section is re-rendered */
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector && e.target.querySelector('[data-nh-intro]');
    if (el && el.__nh) el.__nh.destroy();
  });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    var el = e.target.querySelector('[data-nh-intro]');
    if (el && el.__nh) el.__nh.reset();
  });
})();
